import { useEffect, useState, CSSProperties, ReactNode } from 'react';
import axios from 'axios';
import { displayTransactionName, displayStageThenName } from '../../functions/displayTransactionName';
import { useHistory } from 'react-router-dom';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Button from '../../components/Button';
import FlexContainer from '../../components/FlexContainer';
import getHour from '../../functions/getHour';
import businessDayString from '../../functions/businessDay';

// ── Canonical SSO roster contract (mirrors server/src/sso/sso-contract.ts).
// The page renders strictly from these flags — no boolean coercion or
// review/payment derivation happens here.

interface CanonicalOptIns {
    stage: boolean;
    twoForOne: boolean;
    vipDances: boolean;
    noInteraction: boolean;
}

interface CanonicalProfileStatus {
    complete: boolean;
    needsReview: boolean;
    reviewReasons: string[];
    hasRealDOB: boolean;
    hasLicenseID: boolean;
    ageVerified: boolean;
}

interface CanonicalEntryFee {
    amountDue: number | null;
    paid: boolean;
    transactionId?: string;
    businessDate: string;
    needsReview: boolean;
    reviewReason?: string;
}

interface CanonicalRosterFlags {
    showStageBadge: boolean;
    showTwoForOneBadge: boolean;
    showVipBadge: boolean;
    showNoInteractionBadge: boolean;
    showNeedsReview: boolean;
    showPayButton: boolean;
    showPaidBadge: boolean;
}

interface EntryFeeDetail {
    guestID: string;
    stageName: string;
    businessDate: string;
    weeklyPointsIncludingTonight: number;
    currentPointPeriod: {
        startsAt: string;
        endsAt: string;
    };
    checkedInNights: Array<{
        businessDate: string;
        label: string;
        source?: string;
    }>;
    calculatedAmountDue: number | null;
    baseFee?: number | null;
    regularBaseFeeWouldHaveBeen?: number | null;
    pricingType?: 'regular' | 'vip' | 'special' | 'comp' | null;
    pricingLabel?: string | null;
    specialPricingApplied?: boolean;
    surcharges?: Array<{
        afterHour: number;
        key?: string | null;
        label: string;
        amount: number;
        applies: boolean;
        disabledByGuestPricing?: boolean;
    }>;
    surchargeTotal?: number;
    paid: boolean;
    transactionId?: string;
    paidAmount?: number;
    paidAt?: string;
    paymentSource?: string;
    overrideAmount?: number;
    overrideReason?: string;
    needsReview: boolean;
    reviewReasons: string[];
}

interface RosterEntry {
    // Identity / contact
    id: string;
    guestID: string;
    firstName: string;
    lastName: string;
    stageName: string;
    email: string;
    phoneNumber: string;
    kupartybusUserId: string;
    kupartybusEmailVerified: boolean;
    latestEntry: string;
    purchases?: { vipBand: number; jukebox: number };

    // Canonical contract block
    source?: 'kupartybus';
    sourceGuestId?: string;
    accessType?: 'allAccess' | 'limited';
    optIns?: CanonicalOptIns;
    profile?: CanonicalProfileStatus;
    entryFee?: CanonicalEntryFee;
    rosterFlags?: CanonicalRosterFlags;
    timestamps?: {
        sourceCheckedInAt?: string;
        porkysReceivedAt?: string;
        lastUpdatedAt?: string;
    };
}

// Reusable Badge — outline pills for pink/yellow/green, solid for red.
// Kept local so it's easy to verify; can be lifted to components/Badge.tsx
// later if anything else wants the same visual.

type BadgeVariant = 'pink' | 'yellow' | 'green' | 'red';

const BADGE_VARIANTS: Record<BadgeVariant, CSSProperties> = {
    pink:   { border: '1px solid #ff69b4', color: '#ff69b4', background: 'transparent' },
    yellow: { border: '1px solid #f1c40f', color: '#f1c40f', background: 'transparent' },
    green:  { border: '1px solid #2ecc71', color: '#2ecc71', background: 'transparent' },
    red:    { border: '1px solid #e74c3c', color: '#ffffff', background: '#e74c3c', fontWeight: 800 },
};

const BADGE_BASE: CSSProperties = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
};

const Badge = ({
    variant,
    children,
}: {
    variant: BadgeVariant;
    children: ReactNode;
}) => (
    <span style={{ ...BADGE_BASE, ...BADGE_VARIANTS[variant] }}>{children}</span>
);

// Inline pills shown in the roster's Opt-Ins cell. A flag that is false or
// undefined renders nothing — no dash, no "Opted Out" text.
const OptInPills = ({
    flags,
}: {
    flags: RosterEntry['rosterFlags'];
}) => {
    if (!flags) return null;
    // noInteraction wins — frontend guard. Backend normalizeOptIns
    // already enforces this, but rendering the rule here too keeps
    // the roster consistent even if a deployed backend is older or
    // someone wrote bad fields straight into Mongo.
    const noInt = flags.showNoInteractionBadge === true;
    const showStage = !noInt && flags.showStageBadge === true;
    const show2for1 = !noInt && flags.showTwoForOneBadge === true;
    const showVip = !noInt && flags.showVipBadge === true;
    return (
        <div
            style={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                alignItems: 'center',
            }}
        >
            {showStage && <Badge variant="pink">Stage</Badge>}
            {show2for1 && <Badge variant="yellow">2-for-1</Badge>}
            {showVip && <Badge variant="green">VIP</Badge>}
            {noInt && <Badge variant="red">No Interaction</Badge>}
        </div>
    );
};

interface SaleItemDoc {
    _id: string;
    name: string;
    price: number;
}

const SsoRosterPage = () => {
    const history = useHistory();
    const [rows, setRows] = useState<RosterEntry[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [prices, setPrices] = useState<Record<string, number>>({});
    const [busyKey, setBusyKey] = useState<string | null>(null);

    // Payment modal — populated when staff clicks Pay/Details on a row.
    // The modal is the only place where override input + reason live;
    // the row cell stays minimal.
    const [paymentModalGuest, setPaymentModalGuest] =
        useState<RosterEntry | null>(null);
    const [paymentDetail, setPaymentDetail] =
        useState<EntryFeeDetail | null>(null);
    const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
    const [paymentSubmitting, setPaymentSubmitting] =
        useState<boolean>(false);
    const [overrideAmount, setOverrideAmount] = useState<string>('');
    const [overrideReason, setOverrideReason] = useState<string>('');
    // Reset / Edit state — each has its own submitting guard so a
    // mid-edit reset (or vice versa) can't lose state.
    const [resetReason, setResetReason] = useState<string>('');
    const [resetSubmitting, setResetSubmitting] = useState<boolean>(false);
    const [editAmount, setEditAmount] = useState<string>('');
    const [editReason, setEditReason] = useState<string>('');
    const [editSubmitting, setEditSubmitting] = useState<boolean>(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const load = async () => {
        try {
            const r = await axios.get<RosterEntry[]>('/api/sso/roster');
            // ── TEMP contract tracing ── remove once verified
            for (const row of r.data) {
                // eslint-disable-next-line no-console
                console.log(
                    '[SSO CONTRACT NORMALIZED]',
                    displayStageThenName({ firstName: row.firstName, lastName: row.lastName, stageName: row.stageName }),
                    {
                        optIns: row.optIns,
                        profile: row.profile,
                        entryFee: row.entryFee,
                        rosterFlags: row.rosterFlags,
                    }
                );
            }
            setRows(r.data);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Failed to load roster');
        }
    };

    useEffect(() => {
        load();
        (async () => {
            try {
                const { data } = await axios.get<SaleItemDoc[]>(
                    '/api/sales-items'
                );
                const map: Record<string, number> = {};
                for (const i of data) map[i.name] = i.price;
                setPrices(map);
            } catch {
                /* prices stay {} – buttons still work using 0 fallback */
            }
        })();
    }, []);

    const purchase = async (guest: RosterEntry, fee: 'VIP Band' | 'Jukebox') => {
        const price = prices[fee] ?? 0;
        if (
            !window.confirm(
                `Purchase ${fee} for ${guest.stageName || guest.firstName}? $${price.toFixed(
                    2
                )}`
            )
        )
            return;
        setBusyKey(`${guest.guestID}:${fee}`);
        try {
            await axios.post('/api/transactions', {
                guestID: guest.guestID,
                firstName: guest.firstName || 'SSO',
                lastName: guest.lastName || '—',
                stageName: guest.stageName || 'NONE',
                fee,
                type: 'allAccess',
                value: price,
                points: 0,
                paymentMethod: 'cash',
                quantity: 1,
            });
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Purchase failed.');
        } finally {
            setBusyKey(null);
        }
    };

    const openPaymentModal = async (row: RosterEntry) => {
        setPaymentModalGuest(row);
        setPaymentDetail(null);
        setOverrideAmount('');
        setOverrideReason('');
        setModalError(null);
        setPaymentLoading(true);
        try {
            const r = await axios.get<EntryFeeDetail>(
                `/api/sso/roster/${encodeURIComponent(
                    row.guestID
                )}/entry-fee-detail`
            );
            setPaymentDetail(r.data);
            if (
                r.data.calculatedAmountDue !== null &&
                r.data.calculatedAmountDue !== undefined
            ) {
                setOverrideAmount(String(r.data.calculatedAmountDue));
            }
        } catch (e: any) {
            setModalError(
                e?.response?.data?.message ||
                    'Could not load payment detail.'
            );
        } finally {
            setPaymentLoading(false);
        }
    };

    const closePaymentModal = () => {
        setPaymentModalGuest(null);
        setPaymentDetail(null);
        setOverrideAmount('');
        setOverrideReason('');
        setResetReason('');
        setEditAmount('');
        setEditReason('');
        setModalError(null);
        setPaymentSubmitting(false);
        setResetSubmitting(false);
        setEditSubmitting(false);
    };

    const confirmEntryFeePayment = async () => {
        if (!paymentDetail) return;
        // Re-entry guard — submitting flips synchronously so a fast
        // double-click on Confirm cannot land two POSTs.
        if (paymentSubmitting) return;

        const calculated = Number(paymentDetail.calculatedAmountDue ?? 0);
        const overrideRaw = overrideAmount.trim();
        const override =
            overrideRaw === '' ? undefined : Number(overrideRaw);
        if (
            override !== undefined &&
            (!Number.isFinite(override) || override < 0)
        ) {
            setModalError('Override amount must be a valid number.');
            return;
        }
        const differs =
            override !== undefined &&
            Number(override.toFixed(2)) !== Number(calculated.toFixed(2));
        if (differs && overrideReason.trim().length < 2) {
            setModalError(
                'Override reason is required when changing the fee.'
            );
            return;
        }

        setModalError(null);
        setPaymentSubmitting(true);
        try {
            await axios.post(
                `/api/sso/roster/${encodeURIComponent(
                    paymentDetail.guestID
                )}/pay-entry-fee`,
                {
                    overrideAmount: differs ? override : undefined,
                    overrideReason: differs
                        ? overrideReason.trim()
                        : undefined,
                }
            );
            closePaymentModal();
            await load();
        } catch (e: any) {
            setModalError(
                e?.response?.data?.message ||
                    'Payment failed. Please try again.'
            );
            setPaymentSubmitting(false);
        }
    };

    const resetPayment = async () => {
        if (!paymentDetail) return;
        if (resetSubmitting) return;
        if (resetReason.trim().length < 2) {
            setModalError('Reset reason is required.');
            return;
        }
        setModalError(null);
        setResetSubmitting(true);
        try {
            await axios.post(
                `/api/sso/roster/${encodeURIComponent(
                    paymentDetail.guestID
                )}/reset-entry-fee`,
                { reason: resetReason.trim() }
            );
            closePaymentModal();
            await load();
        } catch (e: any) {
            setModalError(
                e?.response?.data?.message ||
                    'Reset failed. Please try again.'
            );
            setResetSubmitting(false);
        }
    };

    const editPayment = async () => {
        if (!paymentDetail) return;
        if (editSubmitting) return;
        const newAmount = Number(editAmount);
        if (
            editAmount.trim() === '' ||
            !Number.isFinite(newAmount) ||
            newAmount < 0
        ) {
            setModalError('New amount must be a valid non-negative number.');
            return;
        }
        if (editReason.trim().length < 2) {
            setModalError('Edit reason is required.');
            return;
        }
        setModalError(null);
        setEditSubmitting(true);
        try {
            await axios.post(
                `/api/sso/roster/${encodeURIComponent(
                    paymentDetail.guestID
                )}/edit-entry-fee`,
                {
                    newAmount,
                    reason: editReason.trim(),
                }
            );
            closePaymentModal();
            await load();
        } catch (e: any) {
            setModalError(
                e?.response?.data?.message ||
                    'Edit failed. Please try again.'
            );
            setEditSubmitting(false);
        }
    };

    const dateLabel = businessDayString();

    return (
        <Container containerName={'landing'}>
            <Header
                title={"Who's Here"}
                subtitle={'SSO Check-In Roster'}
                bottomBorder={true}
                padding={true}
            />
            <FlexContainer>
                <h3 className="transaction-container__text">
                    Date: <span className="red-span">{dateLabel}</span>
                </h3>

                {error && (
                    <h3 className="transaction-container__text">
                        <span className="red-span">{error}</span>
                    </h3>
                )}

                {!rows && !error && (
                    <h3 className="transaction-container__text">Loading…</h3>
                )}

                {rows && rows.length === 0 && (
                    <h3 className="transaction-container__text">
                        No SSO check-ins yet today.
                    </h3>
                )}

                {rows && rows.length > 0 && (
                    <div
                        style={{
                            width: '100%',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <table
                            className="transaction-container__form--table"
                            style={{ minWidth: '720px' }}
                        >
                            <thead>
                                <tr>
                                    <th>Stage Name</th>
                                    <th>Name</th>
                                    <th>Check-In</th>
                                    <th>Entry Fee</th>
                                    <th>Opt-Ins</th>
                                    <th>VIP Band</th>
                                    <th>Jukebox</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const vipBusy =
                                        busyKey === `${r.guestID}:VIP Band`;
                                    const jukeBusy =
                                        busyKey === `${r.guestID}:Jukebox`;
                                    const vipCount = r.purchases?.vipBand || 0;
                                    const jukeCount = r.purchases?.jukebox || 0;
                                    return (
                                        <tr id={r.id} key={r.id}>
                                            <td className="transaction-container__form--table-stage">
                                                {(
                                                    displayStageThenName({ firstName: r.firstName, lastName: r.lastName, stageName: r.stageName }) || '—'
                                                ).toUpperCase()}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {displayTransactionName({ firstName: r.firstName, lastName: r.lastName, stageName: r.stageName }).toUpperCase()}
                                            </td>
                                            <td className="transaction-container__form--table-time">
                                                {getHour(
                                                    r.latestEntry as unknown as Date
                                                )}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                {(() => {
                                                    const flags =
                                                        r.rosterFlags;
                                                    const ef = r.entryFee;
                                                    const profile =
                                                        r.profile;
                                                    const feeBusy =
                                                        busyKey ===
                                                        `${r.guestID}:EntryFee`;
                                                    if (!flags || !ef) {
                                                        return (
                                                            <span
                                                                style={{
                                                                    color: '#888',
                                                                }}
                                                            >
                                                                —
                                                            </span>
                                                        );
                                                    }
                                                    // Order matters: paid wins
                                                    // over review (a guest who
                                                    // already paid stays paid
                                                    // even if their profile is
                                                    // still flagged).
                                                    if (flags.showPaidBadge) {
                                                        return (
                                                            <span>
                                                                <span
                                                                    className="green-span"
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        marginRight:
                                                                            '0.4rem',
                                                                    }}
                                                                >
                                                                    ✓ Paid
                                                                </span>
                                                                <button
                                                                    onClick={() =>
                                                                        openPaymentModal(
                                                                            r
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        paymentLoading
                                                                    }
                                                                    style={{
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        padding:
                                                                            '2px 8px',
                                                                    }}
                                                                >
                                                                    Edit
                                                                </button>
                                                            </span>
                                                        );
                                                    }
                                                    if (
                                                        flags.showNeedsReview
                                                    ) {
                                                        const reasons = [
                                                            ...(profile?.reviewReasons ||
                                                                []),
                                                            ef.reviewReason ||
                                                                '',
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' • ');
                                                        return (
                                                            <span>
                                                                <span
                                                                    title={
                                                                        reasons ||
                                                                        'Needs review'
                                                                    }
                                                                    style={{
                                                                        border: '1px solid #e67e22',
                                                                        color: '#e67e22',
                                                                        background:
                                                                            'transparent',
                                                                        padding:
                                                                            '4px 10px',
                                                                        borderRadius: 999,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        fontWeight: 600,
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                        marginRight:
                                                                            '0.4rem',
                                                                    }}
                                                                >
                                                                    Needs Review
                                                                </span>
                                                                <button
                                                                    onClick={() =>
                                                                        openPaymentModal(
                                                                            r
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        paymentLoading
                                                                    }
                                                                    style={{
                                                                        fontSize:
                                                                            '0.75rem',
                                                                        padding:
                                                                            '2px 8px',
                                                                    }}
                                                                >
                                                                    Details
                                                                </button>
                                                            </span>
                                                        );
                                                    }
                                                    if (flags.showPayButton) {
                                                        return (
                                                            <span>
                                                                <span
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        marginRight:
                                                                            '0.4rem',
                                                                    }}
                                                                >
                                                                    $
                                                                    {
                                                                        ef.amountDue
                                                                    }
                                                                </span>
                                                                <button
                                                                    onClick={() =>
                                                                        openPaymentModal(
                                                                            r
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        feeBusy ||
                                                                        paymentLoading
                                                                    }
                                                                >
                                                                    Pay /
                                                                    Details
                                                                </button>
                                                            </span>
                                                        );
                                                    }
                                                    return (
                                                        <span
                                                            style={{
                                                                color: '#888',
                                                            }}
                                                        >
                                                            —
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                <OptInPills flags={r.rosterFlags} />
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                <button
                                                    onClick={() =>
                                                        purchase(r, 'VIP Band')
                                                    }
                                                    disabled={vipBusy}
                                                >
                                                    {vipBusy ? '…' : 'Buy'}
                                                </button>
                                                {vipCount > 0 && (
                                                    <span
                                                        className="green-span"
                                                        style={{
                                                            marginLeft:
                                                                '0.5rem',
                                                        }}
                                                    >
                                                        ✓ {vipCount}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="transaction-container__form--table-value">
                                                <button
                                                    onClick={() =>
                                                        purchase(r, 'Jukebox')
                                                    }
                                                    disabled={jukeBusy}
                                                >
                                                    {jukeBusy ? '…' : 'Buy'}
                                                </button>
                                                {jukeCount > 0 && (
                                                    <span
                                                        className="green-span"
                                                        style={{
                                                            marginLeft:
                                                                '0.5rem',
                                                        }}
                                                    >
                                                        ✓ {jukeCount}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push('/kiosk')}
                >
                    Go back
                </Button>
            </FlexContainer>

            {paymentModalGuest && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9000,
                        padding: 16,
                    }}
                    onClick={(e) => {
                        // Backdrop click closes — but only when not
                        // submitting, so a misclick during a
                        // half-second POST doesn't lose the form.
                        if (
                            e.target === e.currentTarget &&
                            !paymentSubmitting
                        ) {
                            closePaymentModal();
                        }
                    }}
                >
                    <div
                        style={{
                            background: '#1f1f1f',
                            color: '#fff',
                            border: '2px solid #444',
                            borderRadius: 12,
                            padding: 24,
                            width: '100%',
                            maxWidth: 520,
                            maxHeight: '90vh',
                            overflowY: 'auto',
                        }}
                    >
                        <h2 style={{ marginTop: 0 }}>
                            Entry Fee Payment
                        </h2>

                        {paymentLoading && (
                            <p
                                style={{
                                    color: '#aaa',
                                    fontStyle: 'italic',
                                }}
                            >
                                Loading details…
                            </p>
                        )}

                        {!paymentLoading && paymentDetail && (
                            <>
                                <p
                                    style={{
                                        fontSize: 18,
                                        margin: '4px 0',
                                    }}
                                >
                                    <strong>Stage Name: </strong>
                                    {displayStageThenName(paymentDetail)}
                                </p>
                                <p
                                    style={{
                                        fontSize: 16,
                                        margin: '4px 0',
                                    }}
                                >
                                    <strong>Weekly Points: </strong>
                                    {
                                        paymentDetail.weeklyPointsIncludingTonight
                                    }
                                </p>
                                <p
                                    style={{
                                        fontSize: 14,
                                        margin: '4px 0',
                                        color: '#bbb',
                                    }}
                                >
                                    <strong>Period: </strong>
                                    {new Date(
                                        paymentDetail.currentPointPeriod.startsAt
                                    ).toLocaleDateString()}{' '}
                                    →{' '}
                                    {new Date(
                                        paymentDetail.currentPointPeriod.endsAt
                                    ).toLocaleDateString()}
                                </p>

                                <div style={{ margin: '12px 0' }}>
                                    <strong>
                                        Checked In This Period:
                                    </strong>
                                    {paymentDetail.checkedInNights.length ===
                                    0 ? (
                                        <p
                                            style={{
                                                color: '#aaa',
                                                margin: '4px 0',
                                            }}
                                        >
                                            No check-ins in this period.
                                        </p>
                                    ) : (
                                        <ul
                                            style={{
                                                margin: '6px 0 0 0',
                                                paddingLeft: 20,
                                            }}
                                        >
                                            {paymentDetail.checkedInNights.map(
                                                (n) => (
                                                    <li
                                                        key={n.businessDate}
                                                        style={{
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {n.label}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    )}
                                </div>

                                <div
                                    style={{
                                        marginTop: 12,
                                        padding: '12px 16px',
                                        background: '#2c3e50',
                                        borderRadius: 8,
                                        textAlign: 'left',
                                    }}
                                >
                                    {paymentDetail.calculatedAmountDue ===
                                    null ? (
                                        <div
                                            style={{
                                                fontSize: 20,
                                                fontWeight: 800,
                                                textAlign: 'center',
                                            }}
                                        >
                                            Calculated Amount Due: —
                                        </div>
                                    ) : (
                                        <>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    color: '#bdc3c7',
                                                    marginBottom: 6,
                                                }}
                                            >
                                                Calculated Amount Due
                                            </div>
                                            {paymentDetail.specialPricingApplied && (
                                                <div
                                                    style={{
                                                        background:
                                                            '#5d3fd3',
                                                        color: '#fff',
                                                        padding: '6px 10px',
                                                        borderRadius: 6,
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        marginBottom: 6,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Pricing Type:{' '}
                                                    {paymentDetail.pricingLabel ||
                                                        paymentDetail.pricingType}
                                                </div>
                                            )}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    fontSize: 16,
                                                    margin: '2px 0',
                                                }}
                                            >
                                                <span>
                                                    {paymentDetail.specialPricingApplied
                                                        ? 'Special Base Fee'
                                                        : 'Base Fee'}
                                                </span>
                                                <strong>
                                                    $
                                                    {(
                                                        paymentDetail.baseFee ??
                                                        paymentDetail.calculatedAmountDue
                                                    ).toFixed(2)}
                                                </strong>
                                            </div>
                                            {paymentDetail.specialPricingApplied &&
                                                typeof paymentDetail.regularBaseFeeWouldHaveBeen ===
                                                    'number' && (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'space-between',
                                                            fontSize: 12,
                                                            color: '#95a5a6',
                                                            margin: '1px 0 4px',
                                                            fontStyle:
                                                                'italic',
                                                        }}
                                                    >
                                                        <span>
                                                            Regular fee would
                                                            have been
                                                        </span>
                                                        <span>
                                                            $
                                                            {paymentDetail.regularBaseFeeWouldHaveBeen.toFixed(
                                                                2
                                                            )}
                                                        </span>
                                                    </div>
                                                )}

                                            {paymentDetail.surcharges &&
                                                paymentDetail.surcharges
                                                    .length > 0 && (
                                                    <>
                                                        <div
                                                            style={{
                                                                fontSize: 13,
                                                                color: '#bdc3c7',
                                                                marginTop: 8,
                                                                marginBottom: 2,
                                                            }}
                                                        >
                                                            Late Check-In
                                                            Surcharges
                                                        </div>
                                                        {paymentDetail.surcharges.map(
                                                            (sc) => {
                                                                const disabled =
                                                                    sc.disabledByGuestPricing ===
                                                                    true;
                                                                const color = sc.applies
                                                                    ? '#fff'
                                                                    : disabled
                                                                    ? '#5d3fd3'
                                                                    : '#7f8c8d';
                                                                const marker = sc.applies
                                                                    ? '✓'
                                                                    : disabled
                                                                    ? '⊘'
                                                                    : '—';
                                                                const right = sc.applies
                                                                    ? `+$${sc.amount.toFixed(
                                                                          2
                                                                      )}`
                                                                    : disabled
                                                                    ? 'Disabled for this guest'
                                                                    : '$0.00';
                                                                return (
                                                                    <div
                                                                        key={
                                                                            sc.afterHour
                                                                        }
                                                                        style={{
                                                                            display:
                                                                                'flex',
                                                                            justifyContent:
                                                                                'space-between',
                                                                            fontSize: 14,
                                                                            margin:
                                                                                '1px 0',
                                                                            color,
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {marker}{' '}
                                                                            {
                                                                                sc.label
                                                                            }
                                                                        </span>
                                                                        <span>
                                                                            {right}
                                                                        </span>
                                                                    </div>
                                                                );
                                                            }
                                                        )}
                                                        <div
                                                            style={{
                                                                display:
                                                                    'flex',
                                                                justifyContent:
                                                                    'space-between',
                                                                fontSize: 14,
                                                                marginTop: 4,
                                                                paddingTop: 4,
                                                                borderTop:
                                                                    '1px solid #34495e',
                                                            }}
                                                        >
                                                            <span>
                                                                Total Surcharge
                                                            </span>
                                                            <strong>
                                                                +$
                                                                {(
                                                                    paymentDetail.surchargeTotal ??
                                                                    0
                                                                ).toFixed(2)}
                                                            </strong>
                                                        </div>
                                                    </>
                                                )}

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    fontSize: 22,
                                                    fontWeight: 800,
                                                    marginTop: 8,
                                                    paddingTop: 8,
                                                    borderTop:
                                                        '2px solid #34495e',
                                                }}
                                            >
                                                <span>Final Due</span>
                                                <span>
                                                    $
                                                    {paymentDetail.calculatedAmountDue.toFixed(
                                                        2
                                                    )}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {paymentDetail.paid && (
                                    <>
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 12,
                                                background: '#27ae60',
                                                borderRadius: 8,
                                                fontWeight: 700,
                                                textAlign: 'left',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 18,
                                                    textAlign: 'center',
                                                    marginBottom: 6,
                                                }}
                                            >
                                                ✓ Already Paid
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                }}
                                            >
                                                Paid Amount:{' '}
                                                <strong>
                                                    {typeof paymentDetail.paidAmount ===
                                                    'number'
                                                        ? `$${paymentDetail.paidAmount.toFixed(
                                                              2
                                                          )}`
                                                        : '—'}
                                                </strong>
                                            </div>
                                            {paymentDetail.transactionId && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Transaction:{' '}
                                                    {paymentDetail.transactionId}
                                                </div>
                                            )}
                                            {paymentDetail.paidAt && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Paid:{' '}
                                                    {new Date(
                                                        paymentDetail.paidAt
                                                    ).toLocaleString()}
                                                </div>
                                            )}
                                            {paymentDetail.paymentSource && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Source:{' '}
                                                    {
                                                        paymentDetail.paymentSource
                                                    }
                                                </div>
                                            )}
                                            {paymentDetail.overrideReason && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Override Reason:{' '}
                                                    {
                                                        paymentDetail.overrideReason
                                                    }
                                                </div>
                                            )}
                                        </div>

                                        {/* Reset section */}
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 12,
                                                border: '1px solid #555',
                                                borderRadius: 8,
                                                background: '#0f0f0f',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 700,
                                                    marginBottom: 8,
                                                    color: '#e74c3c',
                                                }}
                                            >
                                                Reset Payment
                                            </div>
                                            <textarea
                                                rows={2}
                                                value={resetReason}
                                                onChange={(e) =>
                                                    setResetReason(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={
                                                    resetSubmitting ||
                                                    editSubmitting
                                                }
                                                placeholder="Reason for reset (required)"
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    fontSize: 13,
                                                    borderRadius: 6,
                                                    border: '1px solid #555',
                                                    background: '#1f1f1f',
                                                    color: '#fff',
                                                    marginBottom: 8,
                                                }}
                                            />
                                            <Button
                                                color={'red'}
                                                margin={false}
                                                disabled={
                                                    resetSubmitting ||
                                                    editSubmitting
                                                }
                                                onClick={resetPayment}
                                            >
                                                {resetSubmitting
                                                    ? 'Resetting…'
                                                    : 'Reset Payment'}
                                            </Button>
                                        </div>

                                        {/* Edit section */}
                                        <div
                                            style={{
                                                marginTop: 12,
                                                padding: 12,
                                                border: '1px solid #555',
                                                borderRadius: 8,
                                                background: '#0f0f0f',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontWeight: 700,
                                                    marginBottom: 8,
                                                    color: '#f1c40f',
                                                }}
                                            >
                                                Edit Payment
                                            </div>
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontSize: 12,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                New Amount ($)
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                value={editAmount}
                                                onChange={(e) =>
                                                    setEditAmount(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={
                                                    editSubmitting ||
                                                    resetSubmitting
                                                }
                                                placeholder={`e.g. ${
                                                    paymentDetail.paidAmount?.toFixed(
                                                        2
                                                    ) ?? '0.00'
                                                }`}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    fontSize: 14,
                                                    borderRadius: 6,
                                                    border: '1px solid #555',
                                                    background: '#1f1f1f',
                                                    color: '#fff',
                                                    marginBottom: 8,
                                                }}
                                            />
                                            <textarea
                                                rows={2}
                                                value={editReason}
                                                onChange={(e) =>
                                                    setEditReason(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={
                                                    editSubmitting ||
                                                    resetSubmitting
                                                }
                                                placeholder="Reason for edit (required)"
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    fontSize: 13,
                                                    borderRadius: 6,
                                                    border: '1px solid #555',
                                                    background: '#1f1f1f',
                                                    color: '#fff',
                                                    marginBottom: 8,
                                                }}
                                            />
                                            <Button
                                                color={'yellow'}
                                                margin={false}
                                                disabled={
                                                    editSubmitting ||
                                                    resetSubmitting
                                                }
                                                onClick={editPayment}
                                            >
                                                {editSubmitting
                                                    ? 'Editing…'
                                                    : 'Edit Payment'}
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {paymentDetail.needsReview && (
                                    <div
                                        style={{
                                            marginTop: 12,
                                            padding: 12,
                                            background:
                                                'rgba(230,126,34,0.15)',
                                            border: '1px solid #e67e22',
                                            borderRadius: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: '#e67e22',
                                                fontWeight: 700,
                                                marginBottom: 4,
                                            }}
                                        >
                                            Needs Review
                                        </div>
                                        {paymentDetail.reviewReasons.length >
                                        0 ? (
                                            <ul
                                                style={{
                                                    margin: '4px 0 0 0',
                                                    paddingLeft: 20,
                                                    fontSize: 13,
                                                }}
                                            >
                                                {paymentDetail.reviewReasons.map(
                                                    (r, i) => (
                                                        <li key={i}>{r}</li>
                                                    )
                                                )}
                                            </ul>
                                        ) : (
                                            <p
                                                style={{
                                                    margin: 0,
                                                    fontSize: 13,
                                                }}
                                            >
                                                Profile or fee preview
                                                flagged for staff review.
                                            </p>
                                        )}
                                    </div>
                                )}

                                {!paymentDetail.paid && (
                                    <>
                                        <div style={{ marginTop: 16 }}>
                                            <label
                                                style={{
                                                    display: 'block',
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                Override Amount ($)
                                            </label>
                                            <input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                value={overrideAmount}
                                                onChange={(e) =>
                                                    setOverrideAmount(
                                                        e.target.value
                                                    )
                                                }
                                                disabled={paymentSubmitting}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    fontSize: 16,
                                                    borderRadius: 6,
                                                    border: '1px solid #555',
                                                    background: '#0f0f0f',
                                                    color: '#fff',
                                                }}
                                            />
                                        </div>

                                        {(() => {
                                            const calc = Number(
                                                paymentDetail.calculatedAmountDue ??
                                                    0
                                            );
                                            const ovRaw =
                                                overrideAmount.trim();
                                            const ov =
                                                ovRaw === ''
                                                    ? undefined
                                                    : Number(ovRaw);
                                            const diff =
                                                ov !== undefined &&
                                                Number.isFinite(ov) &&
                                                Number(ov.toFixed(2)) !==
                                                    Number(calc.toFixed(2));
                                            if (!diff) return null;
                                            return (
                                                <div
                                                    style={{ marginTop: 12 }}
                                                >
                                                    <label
                                                        style={{
                                                            display: 'block',
                                                            fontWeight: 600,
                                                            marginBottom: 4,
                                                        }}
                                                    >
                                                        Override Reason
                                                        (required)
                                                    </label>
                                                    <textarea
                                                        rows={2}
                                                        value={
                                                            overrideReason
                                                        }
                                                        onChange={(e) =>
                                                            setOverrideReason(
                                                                e.target
                                                                    .value
                                                            )
                                                        }
                                                        disabled={
                                                            paymentSubmitting
                                                        }
                                                        placeholder="Why is the fee different?"
                                                        style={{
                                                            width: '100%',
                                                            padding:
                                                                '8px 10px',
                                                            fontSize: 14,
                                                            borderRadius: 6,
                                                            border:
                                                                '1px solid #555',
                                                            background:
                                                                '#0f0f0f',
                                                            color: '#fff',
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                            </>
                        )}

                        {modalError && (
                            <div
                                style={{
                                    marginTop: 12,
                                    background: '#c0392b',
                                    color: '#fff',
                                    padding: 10,
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    textAlign: 'center',
                                }}
                            >
                                {modalError}
                            </div>
                        )}

                        <div
                            style={{
                                marginTop: 20,
                                display: 'flex',
                                gap: 8,
                                flexDirection: 'column',
                            }}
                        >
                            <Button
                                color={'green'}
                                margin={false}
                                disabled={
                                    paymentLoading ||
                                    paymentSubmitting ||
                                    !paymentDetail ||
                                    paymentDetail.paid === true ||
                                    (paymentDetail.calculatedAmountDue ===
                                        null &&
                                        overrideAmount.trim() === '')
                                }
                                onClick={confirmEntryFeePayment}
                            >
                                {paymentSubmitting
                                    ? 'Processing…'
                                    : 'Confirm Payment'}
                            </Button>
                            <Button
                                color={'red'}
                                margin={false}
                                disabled={paymentSubmitting}
                                onClick={closePaymentModal}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default SsoRosterPage;
